import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Collapse,
  Divider,
  IconButton,
  List,
  ListItemButton, ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CalendarListItem from './CalendarListItem';
import { pushSnackbar } from 'FRONTEND/actions/app';
import { COLORS } from 'FRONTEND/components/Calendar/utils';
import ShrinkableText from 'FRONTEND/components/ShrinkableText';
import { VISUALIZE_MULTIPLE_CALENDAR } from 'FRONTEND/components/SupportDrawer/contentMap';
import { selectCalendarActorId } from 'FRONTEND/reducers/calendarSlice';
import {
  check as checkExpert,
  checkFive as checkFiveExperts,
  unCheck as unCheckExpert,
} from 'FRONTEND/reducers/expertSlice';
import { open as openSupportDrawer } from 'FRONTEND/reducers/supportDrawerSlice';
import { MAX_CHECKED_EXPERT_COUNT } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import {
  CALENDAR_USER_SETTINGS_PATH,
} from 'FRONTEND/utils/routes';

const CalendarList = ({
  onFilterChange,
  onItemClick,
  onSettingsClick,
  onBookClick,
  title,
  titleIcon,
  infoText,
  emptyListText,
  initialIsOpen = false,
  itemList = [],
  shouldHideSearch = false,
  filterText,
  noMultipleCalendar = false,
  itemTooltipTitle,
}) => {
  const dispatch = useDispatch();
  const isExtended = useSelector((state) => state.sidePanel.isExtended);
  const expertCheckedList = useSelector((state) => state.expert.checkedList);
  const [open, setOpen] = useState(initialIsOpen);
  const selectedActorId = useSelector(selectCalendarActorId);

  // Auto-select up to 5 experts on init if multi-view
  useEffect(() => {
    if (!noMultipleCalendar) {
      dispatch(checkFiveExperts({ itemList }));
    }
  }, []);

  const handleFilterTextChange = (newText) => {
    onFilterChange(newText);
  };

  const handleTitleClick = () => {
    setOpen(!open);
  };

  const handleInfoClick = () => {
    dispatch(openSupportDrawer({ contentId: VISUALIZE_MULTIPLE_CALENDAR }));
  };

  const handleItemClick = useCallback((id) => {
    onItemClick(id);
  }, []);

  const handleSettingsClick = useCallback((id) => {
    onSettingsClick(id);
  }, []);

  const handleExpertCheckboxChange = useCallback((isChecked, id) => {
    if (isChecked && expertCheckedList.length >= MAX_CHECKED_EXPERT_COUNT) {
      dispatch(pushSnackbar({
        message: i18n._('You can only display 5 agendas at a time. Please deselect one.'),
        variant: 'warning',
      }));
      return;
    }
    if (isChecked) {
      dispatch(checkExpert({ id }));
    } else {
      dispatch(unCheckExpert({ id }));
    }
  }, [expertCheckedList]);

  return (
    <>
      <Divider />
      {title && (
        <ListItemButton
          onClick={handleTitleClick}
          selected // Not actually selected but to make it stand out.
        >
          {titleIcon && (
            <ListItemIcon>
              {titleIcon}
            </ListItemIcon>
          )}
          <ListItemText primary={isExtended ? '' : title} />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      )}
      <Collapse in={open} timeout='auto' unmountOnExit>
        {isExtended && !shouldHideSearch && (
          <div className='calendarSidePanel-searchBar'>
            <TextField
              fullWidth
              placeholder={i18n._('Search...')}
              variant='outlined'
              size='small'
              value={filterText}
              onChange={(event) => handleFilterTextChange(event.target.value)}
            />
          </div>
        )}
        {isExtended && (
          <div className='calendarSidePanel-text'>
            <Typography variant='body2' align='center'>
              {infoText}
              {!noMultipleCalendar && <IconButton size='small' onClick={handleInfoClick}><InfoOutlinedIcon /></IconButton>}
            </Typography>
          </div>
        )}

        {itemList.length > 0 ? (
          <List dense className='calendarSidePanel-list' disablePadding>
            {itemList.map((item) => {
              const isSettingsOpen = item.id === selectedActorId && window.location.pathname.includes(CALENDAR_USER_SETTINGS_PATH);
              const colorIndex = expertCheckedList.indexOf(item.id);
              return (
                <CalendarListItem
                  key={item.id}
                  item={item}
                  color={COLORS[colorIndex]?.normal}
                  onCheckBoxChange={handleExpertCheckboxChange}
                  onItemClick={handleItemClick}
                  onBookClick={onBookClick}
                  onSettingsClick={handleSettingsClick}
                  disableCheckbox={noMultipleCalendar}
                  size={isExtended ? 'large' : 'small'}
                  itemTooltipTitle={itemTooltipTitle}
                  shouldShowSettings={!isSettingsOpen}
                />
              );
            })}
          </List>
        ) : filterText ? (
          <ShrinkableText isShrunk={!isExtended}>
            {i18n._('Your search doesn’t match any practitioner.')}
          </ShrinkableText>
        ) : (
          <ShrinkableText isShrunk={!isExtended}>
            {emptyListText}
          </ShrinkableText>
        )}
      </Collapse>
    </>
  );
};

export default CalendarList;
